import { Icon } from "@circle-react-shared/Icon";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Dialog, Transition } from "@headlessui/react";
import type { ComponentPropsWithoutRef } from "react";
import { Fragment, useRef } from "react";

import { legacyClassNames } from "@/react/helpers/cssAnchorHelpers";

const invisibleCharacter = "&#8203;";

export interface LargeModalProps {
  children: React.ReactNode;
  className?: string;
  hideCloseButton?: boolean;
  onClose: () => void;
  passThroughProps?: Partial<ComponentPropsWithoutRef<typeof Dialog>>;
  show: boolean;
  title: string;
}

/** @deprecated Please use the new Modal components at "@circle-react-uikit/ModalV2" */
export const LargeModal = ({
  show,
  title,
  onClose,
  hideCloseButton,
  className,
  passThroughProps,
  children,
}: LargeModalProps) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        static
        className={classNames(
          legacyClassNames("fullpage-modal"),
          "fixed inset-0 z-10 overflow-y-auto",
          className,
        )}
        initialFocus={cancelButtonRef}
        open={show}
        onClose={onClose}
        {...passThroughProps}
      >
        <div
          className={classNames(
            legacyClassNames("fullpage-modal__body-outer"),
            "size-full",
          )}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-900/50" />
          </Transition.Child>
          <span
            className="hidden"
            aria-hidden="true"
            style={{ content: invisibleCharacter }}
          />
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className={classNames(
                legacyClassNames("z-1 fullpage-modal__body-wrap"),
                "relative m-0 size-full",
              )}
            >
              <div
                className={classNames(
                  legacyClassNames("fullpage-modal__body"),
                  "bg-primary h-full rounded-none md:rounded-md",
                )}
                data-testid="modal"
              >
                <h2
                  className={classNames(
                    legacyClassNames("fullpage-modal__title"),
                    "sticky",
                  )}
                >
                  {title}
                </h2>
                {!hideCloseButton && (
                  <button
                    type="submit"
                    className={classNames(
                      legacyClassNames("fullpage-modal__close-icon"),
                      "z-10",
                    )}
                    onClick={onClose}
                    ref={cancelButtonRef}
                  >
                    <Icon type="close" useWithFillCurrentColor />
                  </button>
                )}
                {children}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
